import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Label, Heading, Text } from '../ui/Text';
import { PasswordResetModal } from './PasswordResetModal';

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { error } = useToast();
  const [attemptCount, setAttemptCount] = useState(0);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      rememberMe: false,
    },
    mode: 'onBlur',
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      // Ensure rememberMe has a default value when sending to API
      const loginData = {
        ...data,
        rememberMe: data.rememberMe ?? false,
      };
      await login(loginData);

      // Reset attempt count on successful login
      setAttemptCount(0);

      // Navigate to intended destination or dashboard
      const from =
        (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch {
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);

      // Clear only the password field for security, preserve email
      setValue('password', '');

      // Show different messages based on attempt count
      if (newAttemptCount >= 5) {
        // Account is locked
        error(
          'Account temporarily locked for 15 minutes due to security.',
          'Reset your password for immediate access.',
          {
            action: {
              label: 'Reset Password',
              onClick: () => setShowPasswordReset(true),
            },
          }
        );
      } else if (newAttemptCount === 4) {
        // Final warning before lockout
        error(
          'Final warning: Account locks after 1 more attempt.',
          'Consider resetting your password to avoid lockout.',
          {
            action: {
              label: 'Reset Password',
              onClick: () => setShowPasswordReset(true),
            },
          }
        );
      } else if (newAttemptCount === 3) {
        // First warning
        error(
          'Too many incorrect attempts.',
          'Account will be temporarily locked after 2 more failures.',
          {
            action: {
              label: 'Forgot Password?',
              onClick: () => setShowPasswordReset(true),
            },
          }
        );
      } else {
        // Standard error for first 2 attempts
        error('Invalid email or password.', 'Please check your credentials and try again.');
      }
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card shadow-card-hover rounded-lg px-8 py-10 border border-border">
        <div className="text-center mb-8">
          <div className="text-4xl mb-4">🌱</div>
          <Heading as="h1" size="md">
            Welcome back
          </Heading>
          <Text variant="body" className="mt-2">
            Sign in to your PlantQR account
          </Text>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              error={errors.email?.message}
              {...register('email')}
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <button
                type="button"
                onClick={() => setShowPasswordReset(true)}
                className="text-sm font-medium text-green-600 hover:text-green-500"
              >
                Forgot password?
              </button>
            </div>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              error={errors.password?.message}
              {...register('password')}
            />
          </div>

          <div className="flex items-center">
            <input
              id="rememberMe"
              type="checkbox"
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-border rounded"
              {...register('rememberMe')}
            />
            <label htmlFor="rememberMe" className="ml-2 block text-sm text-foreground">
              Remember me for 30 days
            </label>
          </div>

          <Button type="submit" className="w-full" size="lg" isLoading={isSubmitting}>
            Sign in
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="font-medium text-green-600 hover:text-green-500">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      <PasswordResetModal isOpen={showPasswordReset} onClose={() => setShowPasswordReset(false)} />
    </div>
  );
}
